import { Badge, Box, Flex, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconBuildingSkyscraper,
  IconChartBar,
  IconChartLine,
  IconGlobe,
  IconUsers,
  IconWorld,
  type TablerIcon,
} from "@tabler/icons-react";
import CountUp from "react-countup";
import type { SectorEcosystemOverview } from "../../../../../../shared/sectorEcosystemOverview";
import type { SectorVisualConfig } from "../sectorEcosystem.const";
import {
  getStaticSectorCompanyStats,
  getStaticSectorCountryCount,
  getStaticSectorSuccessStories,
  getStaticSectorSuccessStoriesTotal,
} from "../sectorEcosystem.const";

type MetricDef = {
  label: string;
  value?: number;
  comingSoon?: boolean;
  icon: TablerIcon;
  color: string;
  bg: string;
};

function AnimatedCount({ value, countKey }: { value: number; countKey: string }) {
  return (
    <Text fw={700} fz="lg" lh={1.1} component="span" display="block">
      <CountUp key={countKey} start={0} end={value} duration={1.4} suffix="+" />
    </Text>
  );
}

const SUCCESS_STORY_TILES = [
  {
    key: "introductions" as const,
    label: "Introductions",
    icon: IconChartLine,
    bg: "#DCFCE7",
    color: "#166534",
  },
  {
    key: "businessMeetings" as const,
    label: "Business Meetings",
    icon: IconChartBar,
    bg: "#DBEAFE",
    color: "#1D4ED8",
  },
  {
    key: "crossBorderConnections" as const,
    label: "Cross Border Connections",
    icon: IconWorld,
    bg: "#EDE9FE",
    color: "#6D28D9",
  },
];

type SectorEcosystemHeroProps = {
  slug: string;
  overview: SectorEcosystemOverview;
  visual: SectorVisualConfig;
};

export default function SectorEcosystemHero({ slug, overview, visual }: SectorEcosystemHeroProps) {
  const SectorIcon = visual.icon;
  const companyStats = getStaticSectorCompanyStats(slug);
  const successStories = getStaticSectorSuccessStories(slug);
  const successStoriesTotal = getStaticSectorSuccessStoriesTotal(slug);
  const metrics: MetricDef[] = [
    {
      label: "Companies",
      value: companyStats.total,
      icon: IconBuildingSkyscraper,
      color: "#2563EB",
      bg: "#EFF6FF",
    },
    {
      label: "Ecosystem Heads",
      comingSoon: true,
      icon: IconUsers,
      color: "#7C3AED",
      bg: "#F5F3FF",
    },
    {
      label: "Countries",
      value: getStaticSectorCountryCount(slug),
      icon: IconGlobe,
      color: "#16A34A",
      bg: "#F0FDF4",
    },
    {
      label: "Success Stories",
      value: successStoriesTotal,
      icon: IconChartBar,
      color: "#F97316",
      bg: "#FFEDD5",
    },
  ];

  return (
    <Paper
      px={{ base: "md", sm: "xl" }}
      pt={15}
      py={15}
      style={{
        borderTopLeftRadius: 'var(--mantine-radius-md)',
        borderTopRightRadius: 'var(--mantine-radius-md)',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        position: "relative",
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
           <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="xl"
      >
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="sm" align="center" wrap="wrap">
            <ThemeIcon size={40} radius="md" style={{ backgroundColor: visual.iconBg, color: visual.iconColor }}>
              <SectorIcon size={22} />
            </ThemeIcon>
            <Title order={1} fz={{ base: 15, sm: 24 }} fw={500} c="#0F172A" lh={1} mt={0}>
              {overview.title}
            </Title>
            <Badge color="green" variant="light" size="xl" radius="sm">
              Active
            </Badge>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <Paper key={metric.label} p="sm" radius="md" withBorder style={{ borderColor: "#E2E8F0" }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size={32} radius="md" style={{ backgroundColor: metric.bg, color: metric.color }}>
                      <Icon size={16} />
                    </ThemeIcon>
                    <Box>
                      {metric.comingSoon ? (
                        <Text fw={700} fz="sm" c="#64748B" lh={1.2}>
                          Coming Soon
                        </Text>
                      ) : (
                        <AnimatedCount value={metric.value ?? 0} countKey={`${slug}-${metric.label}`} />
                      )}
                      <Text size="xs" c="dimmed">
                        {metric.label}
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
            {SUCCESS_STORY_TILES.map((tile) => {
              const Icon = tile.icon;
              const value = successStories[tile.key];
              return (
                <Paper key={tile.key} p="sm" radius="md" withBorder style={{ borderColor: "#E2E8F0" }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size={32} radius="md" style={{ backgroundColor: tile.bg, color: tile.color }}>
                      <Icon size={16} />
                    </ThemeIcon>
                    <Box>
                      <AnimatedCount value={value} countKey={`${slug}-${tile.key}`} />
                      <Text size="xs" c="dimmed">
                        {tile.label}
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
        <Box
          visibleFrom="md"
          w={280}
          h={180}
          style={{
            flexShrink: 0,
            borderRadius: 12,
            backgroundColor: visual.iconBg,
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SectorIcon size={96} color={visual.iconColor} style={{ opacity: 0.35 }} />
        </Box>
      </Flex>
    </Paper>
  );
}
